import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useParams } from "react-router-dom"
import { useQueryClient } from "@tanstack/react-query"
import { FaArrowUp, FaHistory } from "react-icons/fa"
import { FaArrowUpRightFromSquare } from "react-icons/fa6"
import { LuX } from "react-icons/lu"
import { Badge, Box, Dialog, Drawer, HStack, IconButton, Input, Portal, Separator, Spinner, Stack, Table, Tabs, VStack } from "@chakra-ui/react"
import { Button, Text, toaster } from "@/components"
import { useAuth } from "@/contexts"
import {
  useCompanyInfo,
  useDealInfo,
  useDealLogs,
  useSyncDealFromLegacy,
} from "@/hooks"
import { CompanyFlags, CompanyTabs, DealClientInfo as DealClientInfoView } from "@/features/esoPerson/companies/components"
import { CompanyAssessmentPanel } from "@/features/esoPerson/companies/components/CompanyAssessmentPanel"
import { DealAssessmentPanel } from "./components/DealAssessmentPanel"
import { PortalRole, DealClientInfo as DealClientInfoType, DealClient } from "@/types"
import { previewDealGrants, confirmDealGrants, sendIntroEmail, downloadDealMemo } from "@/api"
import { upsertDealClientInfo, updateDealClient, syncDealPunchlist, getPortalUrl } from "@/api/deals"

const PORTAL_EMAIL_ADMINS = new Set(["sean@esofund.com", "bill.yu@esofund.com", "jordan@esofund.com"])

const PORTAL_STEP_LABELS: Record<string, string> = {
  complete_nda_form: "NDA Form",
  sign_nda: "Sign NDA",
  upload_grant_docs: "Upload Grants",
  accept_offer: "Accept Offer",
  complete_bc_form: "BC Form",
  sign_bc: "Sign BC",
  upload_closing_docs: "Upload Docs",
  schedule_closing_meeting: "Schedule Closing",
  closing: "Closing",
  sign_contract: "Sign Contract",
  send_wire_info: "Wire Info",
  closed: "Closed",
}

// ─── Calculator helpers ────────────────────────────────────────────────────────

type CalcCell = { id: string; label: string; formula: string }

const DEFAULT_CALC_CELLS: CalcCell[] = [
  { id: "c1", label: "", formula: "" },
  { id: "c2", label: "", formula: "" },
  { id: "c3", label: "", formula: "" },
  { id: "c4", label: "", formula: "" },
  { id: "c5", label: "", formula: "" },
]

function evalCellFormula(
  formula: string,
  allCells: CalcCell[],
  computedSoFar: Record<string, number | null>
): number | null {
  if (!formula.trim()) return null
  try {
    let expr = formula.trim()
    for (const c of allCells) {
      if (!c.label.trim()) continue
      const val = computedSoFar[c.id]
      if (val !== null && val !== undefined) {
        const escaped = c.label.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")
        expr = expr.replace(new RegExp(`\\$${escaped}(?![\\w])`, "g"), String(val))
      }
    }
    // eslint-disable-next-line no-new-func
    const result = new Function(`"use strict"; return (${expr})`)()
    return typeof result === "number" && isFinite(result) ? result : null
  } catch {
    return null
  }
}

function computeCalcValues(cells: CalcCell[]): Record<string, number | null> {
  const out: Record<string, number | null> = {}
  for (const cell of cells) {
    out[cell.id] = evalCellFormula(cell.formula, cells, out)
  }
  return out
}

function formatCalcResult(v: number | null): string {
  if (v === null) return "—"
  const abs = Math.abs(v)
  const decimals = abs >= 1000 ? 0 : abs >= 1 ? 2 : 4
  return v.toLocaleString("en-US", { maximumFractionDigits: decimals })
}

// ─── Tier color helpers ────────────────────────────────────────────────────────

const tierColor = (t: string) =>
  t === "A" ? "#22c55e" : t === "B" ? "#3b82f6" : t === "C" ? "#f59e0b" : "#ef4444"
const tierBg = (t: string) =>
  t === "A" ? "rgba(34,197,94,0.1)" : t === "B" ? "rgba(59,130,246,0.1)" : t === "C" ? "rgba(245,158,11,0.1)" : "rgba(239,68,68,0.1)"

type PreviewData = Awaited<ReturnType<typeof previewDealGrants>>

const formatLogDate = (dateString: string) => {
  const d = new Date(dateString)
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" })
}

// ─── Component ────────────────────────────────────────────────────────────────

export const DealManagerDealComponent = () => {
  const params = useParams<{ dealId: string }>()
  const dealId = params.dealId
  const queryClient = useQueryClient()

  const { user } = useAuth()
  const { data: deal, isLoading: isDealLoading } = useDealInfo(dealId)

  const canSendPortalEmail = (() => {
    if (!user || !deal) return false
    const userEmail = (user as any).email as string | undefined
    const userId = (user as any).id as number | undefined
    return PORTAL_EMAIL_ADMINS.has(userEmail ?? "") || userId === deal.dealManagerId
  })()
  const { data: companyInfo, isLoading: isCompanyInfoLoading } = useCompanyInfo(deal?.companyId)
  const { data: logsData } = useDealLogs(dealId)

  const { mutate: syncFromLegacy, isPending: isSyncing } = useSyncDealFromLegacy()

  const [isDrawerOpen, setIsDrawerOpen] = useState(false)
  const [isSyncDialogOpen, setIsSyncDialogOpen] = useState(false)
  const [isCalcOpen, setIsCalcOpen] = useState(false)
  const [leftTab, setLeftTab] = useState("company")
  const [calcCells, setCalcCells] = useState<CalcCell[]>(DEFAULT_CALC_CELLS)

  // Grant preview modal state
  const [isExtracting, setIsExtracting] = useState(false)
  const [grantPreview, setGrantPreview] = useState<PreviewData | null>(null)
  const [editableGrants, setEditableGrants] = useState<PreviewData["grants"]>([])
  const [isConfirming, setIsConfirming] = useState(false)

  // Intro email state
  const [isSendingIntroEmail, setIsSendingIntroEmail] = useState(false)
  const [isIntroEmailConfirmOpen, setIsIntroEmailConfirmOpen] = useState(false)

  // Company assessment state
  const [assessmentOpen, setAssessmentOpen] = useState(false)

  // Deal assessment state
  const [dealAssessmentOpen, setDealAssessmentOpen] = useState(false)

  // Deal memo download state
  const [isDownloadingMemo, setIsDownloadingMemo] = useState(false)

  const sectionRef = useRef<HTMLDivElement>(null)

  // ── Auto-sync punchlist from Dropbox on deal load ──────────────────────────
  useEffect(() => {
    if (!deal?.id) return
    syncDealPunchlist(deal.id)
      .then(() => queryClient.invalidateQueries({ queryKey: ["dealInfo", String(deal.id)] }))
      .catch(() => {}) // silent — punchlist sync is best-effort
  }, [deal?.id]) // eslint-disable-line react-hooks/exhaustive-deps

  const scrollToTop = () => {
    sectionRef.current?.scrollTo({ top: 0, behavior: "smooth" })
  }

  // ── Debounced client info handlers (for left panel Client Info tab) ──────────

  const clientInfoTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const handleClientInfoUpdate = useCallback(
    (field: keyof DealClientInfoType, value: string | number | null) => {
      if (!deal) return
      if (clientInfoTimerRef.current) clearTimeout(clientInfoTimerRef.current)
      clientInfoTimerRef.current = setTimeout(async () => {
        try {
          await upsertDealClientInfo(deal.id, { [field]: value })
          queryClient.invalidateQueries({ queryKey: ["dealInfo", String(deal.id)] })
        } catch {
          toaster.create({ title: "Failed to save client info", type: "error" })
        }
      }, 500)
    },
    [deal, queryClient]
  )

  const clientTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const handleClientUpdate = useCallback(
    (field: keyof DealClient, value: string | number | null) => {
      if (!deal) return
      if (clientTimerRef.current) clearTimeout(clientTimerRef.current)
      clientTimerRef.current = setTimeout(async () => {
        try {
          await updateDealClient(deal.id, { [field]: value })
          queryClient.invalidateQueries({ queryKey: ["dealInfo", String(deal.id)] })
        } catch {
          toaster.create({ title: "Failed to save client data", type: "error" })
        }
      }, 500)
    },
    [deal, queryClient]
  )

  // ── Calculator helpers ────────────────────────────────────────────────────────

  const calcValues = useMemo(() => computeCalcValues(calcCells), [calcCells])

  const addCalcCell = () => {
    setCalcCells((prev) => [...prev, { id: `cell-${Date.now()}`, label: "", formula: "" }])
  }

  const removeCalcCell = (id: string) => {
    setCalcCells((prev) => prev.filter((c) => c.id !== id))
  }

  const updateCalcCell = (id: string, field: "label" | "formula", value: string) => {
    setCalcCells((prev) => prev.map((c) => (c.id === id ? { ...c, [field]: value } : c)))
  }

  // ── Other handlers ────────────────────────────────────────────────────────────

  const handleSendIntroEmail = async () => {
    if (!deal) return
    setIsSendingIntroEmail(true)
    try {
      const result = await sendIntroEmail(deal.id)
      toaster.create({
        title: `Intro email sent to ${result.clientName}`,
        type: "success",
        duration: 3000,
      })
      queryClient.invalidateQueries({ queryKey: ["dealInfo", dealId] })
    } catch (err: any) {
      const msg = err?.response?.data?.error || "Failed to send intro email"
      toaster.create({ title: msg, type: "error", duration: 4000 })
    } finally {
      setIsSendingIntroEmail(false)
    }
  }

  const handleDownloadMemo = async () => {
    if (!deal || !companyInfo) return
    setIsDownloadingMemo(true)
    try {
      const dealName = `${deal.client.firstName} ${deal.client.lastName} - ${companyInfo.name}`
      await downloadDealMemo(deal.id, dealName)
    } catch (err: any) {
      const msg =
        err?.response?.status === 404
          ? "No assessment data available. Run company and deal assessments first."
          : "Failed to generate deal memo PDF"
      toaster.create({ title: msg, type: "error", duration: 4000 })
    } finally {
      setIsDownloadingMemo(false)
    }
  }

  const handleRefreshGrants = async () => {
    if (!deal) return
    setIsExtracting(true)
    try {
      const preview = await previewDealGrants(deal.id)
      setGrantPreview(preview)
      setEditableGrants(preview.grants.map(g => ({ ...g })))
    } catch {
      toaster.create({
        title: "Failed to extract grants from documents",
        type: "error",
        duration: 3000,
      })
    } finally {
      setIsExtracting(false)
    }
  }

  const updateEditableGrant = (index: number, updates: Partial<PreviewData["grants"][0]>) => {
    setEditableGrants(prev => {
      const next = [...prev]
      next[index] = { ...next[index], ...updates }
      // Recompute tax when est. options value, price, or vested changes for NSOs
      const g = next[index]
      if (g.grantType === "NSO" && ("estimatedOptionsValue" in updates || "pricePerShare" in updates || "vestedShares" in updates)) {
        if (g.estimatedOptionsValue && g.pricePerShare && g.vestedShares) {
          next[index].tax = Math.max(0, g.estimatedOptionsValue - (g.pricePerShare * g.vestedShares))
        } else {
          next[index].tax = null
        }
      }
      return next
    })
  }

  const handleConfirmGrants = async () => {
    if (!deal || !grantPreview) return
    setIsConfirming(true)
    try {
      const confirmGrants = editableGrants
        .filter((g) => g.vestedShares !== null && g.pricePerShare !== null)
        .map((g) => ({
          grantDescription: g.grantDescription || "Grant",
          grantType: g.grantType,
          vestedShares: g.vestedShares!,
          pricePerShare: g.pricePerShare!,
          grantDate: g.grantDate || "Unknown",
          existingGrantId: g.existingGrantId,
          tax: g.tax,
        }))
      const confirmFmv = grantPreview.fmv
        ? { fmv: grantPreview.fmv.fmv, date: grantPreview.fmv.date }
        : null

      await confirmDealGrants(deal.id, confirmGrants, confirmFmv)
      queryClient.invalidateQueries({ queryKey: ["dealInfo"] })
      queryClient.invalidateQueries({ queryKey: ["companyInfo"] })
      queryClient.invalidateQueries({ queryKey: ["dealLogs"] })
      setGrantPreview(null)
      toaster.create({
        title: "Grants created from documents",
        type: "success",
        duration: 3000,
      })
    } catch {
      toaster.create({
        title: "Failed to save grants",
        type: "error",
        duration: 3000,
      })
    } finally {
      setIsConfirming(false)
    }
  }

  const handleSyncFromLegacy = () => {
    if (!deal) return
    syncFromLegacy(deal.id, {
      onSuccess: (result) => {
        setIsSyncDialogOpen(false)
        if (result.stageUpdated) {
          toaster.create({
            title: `Stage updated: ${result.previousStage} → ${result.newStage}`,
            description: result.signals[0],
            type: "info",
            duration: 5000,
          })
        } else {
          toaster.create({
            title: "Deal synced from legacy database",
            type: "success",
            duration: 3000,
          })
        }
      },
      onError: () => {
        setIsSyncDialogOpen(false)
        toaster.create({
          title: "Failed to sync deal from legacy database",
          type: "error",
          duration: 3000,
        })
      },
    })
  }

  const { mostRecentFmv, fmvPercentChange } = useMemo(() => {
    const fmvs = companyInfo?.fmvs ?? []
    const type1Fmvs = fmvs.filter((f: any) => f.fmvTypeId === 1 && f.value && f.date)
    if (type1Fmvs.length === 0) return { mostRecentFmv: 0, fmvPercentChange: null as number | null }
    type1Fmvs.sort((a: any, b: any) => new Date(b.date).getTime() - new Date(a.date).getTime())
    const latest = parseFloat(type1Fmvs[0].value) || 0
    const previousFmv = type1Fmvs.find((f: any) => f.value !== type1Fmvs[0].value)
    let pctChange: number | null = null
    if (previousFmv) {
      const prev = parseFloat(previousFmv.value) || 0
      if (prev !== 0) pctChange = ((latest - prev) / prev) * 100
    }
    return { mostRecentFmv: latest, fmvPercentChange: pctChange }
  }, [companyInfo?.fmvs])

  const combinedLogs = useMemo(() => {
    const dealLogs = logsData.dealLogs.map((l) => ({
      id: `deal-${l.id}`,
      type: "deal" as const,
      description: l.description,
      createdAt: l.createdAt,
      by: l.esoPerson?.name || null,
    }))
    const companyLogs = logsData.companyLogs.map((l) => ({
      id: `company-${l.id}`,
      type: "company" as const,
      description: l.description,
      createdAt: l.createdAt,
      by: l.esoPerson?.name || null,
    }))
    const hotlistLogs = (logsData.hotlistLogs || []).map((l) => {
      const actionLabel = l.action === "add" ? "Added to" : l.action === "remove" ? "Removed from" : "Re-added to"
      const desc = `${actionLabel} hotlist${l.reason ? `: ${l.reason}` : ""}`
      return {
        id: `hotlist-${l.id}`,
        type: "hotlist" as const,
        description: desc,
        createdAt: l.createdAt,
        by: l.performedBy || null,
      }
    })
    return [...dealLogs, ...companyLogs, ...hotlistLogs].sort(
      (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    )
  }, [logsData])

  if (!user) {
    return <div>Loading...</div>
  }

  if (user.userType !== PortalRole.ESO_PERSON) {
    return <div>Please contact support to be assigned a deal manager.</div>
  }

  if (isDealLoading || isCompanyInfoLoading) {
    return <div>Loading deal info...</div>
  }

  if (!deal || !companyInfo) {
    return <div>No deal info found.</div>
  }

  const backupManager = (deal as any).irrDeal?.backupManager as { name: string; shortName: string } | null | undefined

  // ── Expiration helpers ─────────────────────────────────────────────────────
  const terminalStages = new Set(["Closed", "ESO Passed", "Client Passed", "Robo Reject"])
  const isDealTerminal = terminalStages.has(deal.dealStage?.name ?? "")

  const expirationDays = deal.expirationDate && !isDealTerminal
    ? Math.floor((new Date(deal.expirationDate).getTime() - Date.now()) / 86400000)
    : null

  const expirationBadgeColor =
    expirationDays === null ? null
    : expirationDays < 0 ? "red"
    : expirationDays <= 7 ? "red"
    : expirationDays <= 30 ? "orange"
    : "gray"

  const expirationLabel =
    expirationDays === null ? null
    : expirationDays < 0 ? "Expired"
    : `${expirationDays}d left`

  const isStale = Math.floor((Date.now() - new Date(deal.updatedAt).getTime()) / 86400000) > 30

  // ── Company/deal assessment data ───────────────────────────────────────────
  const companyAssessment = companyInfo.assessment as {
    overallScore?: number
    tier?: string
    signal?: string
    capTableGateFailed?: boolean
    investmentOneLiner?: string | null
  } | null | undefined
  const isGreat = companyInfo.isGreat as boolean | undefined
  const latestDealAssessment = (deal as any).dealAssessments?.[0] as {
    overallScore?: number | null
    tier?: string | null
    dataConfidence?: string | null
  } | null | undefined

  return (
    <>
      {/* ── Full-height 3-panel layout ─────────────────────────────────────── */}
      <Box display="flex" flexDirection="column" h="100vh" overflow="hidden">
        {/* 3-panel row */}
        <Box flex={1} display="flex" overflow="hidden">

          {/* ── Left Panel: Client Profile ───────────────────────────────── */}
          <Box
            w="300px"
            minW="300px"
            borderRightWidth="1px"
            pt={5}
            borderColor="border.default"
            overflowY="auto"
            flexShrink={0}
          >
            <Stack gap={0} p={4}>
              {/* Client name + DM avatar */}
              <HStack gap={3} align="flex-start" mb={1}>
                {deal.dealManager.profilePicture ? (
                  <Box
                    as="img"
                    src={deal.dealManager.profilePicture}
                    alt={deal.dealManager.name}
                    w="40px"
                    h="40px"
                    borderRadius="full"
                    objectFit="cover"
                    flexShrink={0}
                    mt="2px"
                  />
                ) : (
                  <Box
                    w="40px"
                    h="40px"
                    borderRadius="full"
                    bg="gray.200"
                    flexShrink={0}
                    mt="2px"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                  >
                    <Text fontSize="sm" fontWeight="bold" color="gray.600">
                      {deal.dealManager.name.charAt(0)}
                    </Text>
                  </Box>
                )}
                <Box>
                  <Text fontSize="xl" fontWeight="bold" lineHeight="tight">
                    {deal.client.firstName} {deal.client.lastName}
                  </Text>

                  {/* Title @ Company */}
                  <Text fontSize="sm" color="text.muted">{deal.clientInfo?.title}</Text>
                  <Text
                    fontSize="sm"
                    as="a"
                    href={`/companies/${companyInfo.id}`}
                    target="_blank"
                    color="blue.500"
                    _hover={{ textDecoration: "underline" }}
                  >
                    {companyInfo.name}
                  </Text>
                  {backupManager?.shortName && (
                    <Text fontSize="xs" color="text.muted" mt={0.5}>
                      Back Up: {backupManager.shortName.charAt(0).toUpperCase() + backupManager.shortName.slice(1)}
                    </Text>
                  )}
                </Box>
              </HStack>

              {/* Badge row */}
              <HStack gap={1.5} flexWrap="wrap" my={3}>
                <Badge size="sm" colorPalette="blue" borderRadius="full">
                  {deal.dealStage?.name}
                </Badge>
                {deal.client?.introEmailSentAt && deal.portalStage && (
                  <Badge size="sm" colorPalette="purple" borderRadius="full">
                    Portal: {PORTAL_STEP_LABELS[deal.portalStage] || deal.portalStage}
                  </Badge>
                )}
                {expirationLabel && (
                  <Badge size="sm" colorPalette={expirationBadgeColor ?? "gray"} borderRadius="full">
                    {expirationLabel}
                  </Badge>
                )}
                {isStale && (
                  <Badge size="sm" colorPalette="orange" borderRadius="full">
                    Stale
                  </Badge>
                )}
              </HStack>

              {/* View Deal In Portal */}
              <Button
                size="sm"
                variant="outline"
                width="100%"
                mt={2}
                onClick={async () => {
                  try {
                    const url = await getPortalUrl(deal.id)
                    window.open(url, "_blank")
                  } catch (err: any) {
                    const msg = err?.response?.data?.error
                    toaster.create({
                      title: "Could not open portal",
                      description: msg === "Deal not found in legacy database"
                        ? "This deal hasn't been synced to the portal yet"
                        : msg || undefined,
                      type: "error",
                    })
                  }
                }}
              >
                View Deal In Portal <FaArrowUpRightFromSquare />
              </Button>

              {/* 3-tab section */}
              <Tabs.Root
                mt={5}
                value={leftTab}
                onValueChange={(d) => setLeftTab(d.value)}
                variant="line"
                size="sm"
              >
                <Tabs.List borderColor="border.default">
                  <Tabs.Trigger value="company">Company</Tabs.Trigger>
                  <Tabs.Trigger value="client-info">Client Info</Tabs.Trigger>
                </Tabs.List>

                {/* Company tab */}
                <Tabs.Content value="company">
                  <Stack gap={3} pt={3}>
                    {/* FMV stats */}
                    {mostRecentFmv > 0 && (
                      <HStack gap={5} alignItems="flex-start">
                        <Stack gap={0}>
                          <Text fontSize="2xs" fontWeight="semibold" color="text.muted" textTransform="uppercase" letterSpacing="0.04em">Current FMV</Text>
                          <Text fontSize="md" fontWeight="bold">${mostRecentFmv.toFixed(2)}</Text>
                        </Stack>
                        {fmvPercentChange !== null && (
                          <Stack gap={0}>
                            <Text fontSize="2xs" fontWeight="semibold" color="text.muted" textTransform="uppercase" letterSpacing="0.04em">Change</Text>
                            <Text fontSize="md" fontWeight="bold" color={fmvPercentChange >= 0 ? "green.500" : "red.500"}>
                              {fmvPercentChange >= 0 ? "+" : ""}{fmvPercentChange.toFixed(1)}%
                            </Text>
                          </Stack>
                        )}
                      </HStack>
                    )}

                    {/* Scorecard badges */}
                    <HStack gap={2} flexWrap="wrap" alignItems="flex-start">
                      <VStack gap={0.5} alignItems="flex-start">
                        <Text fontSize="10px" fontWeight="600" color="text.muted" letterSpacing="0.05em" textTransform="uppercase">Company</Text>
                        <Box
                          display="inline-flex"
                          alignItems="center"
                          px={3}
                          py={1}
                          borderRadius="full"
                          cursor="pointer"
                          onClick={() => setAssessmentOpen(true)}
                          transition="all 0.15s"
                          _hover={{ opacity: 0.8 }}
                          {...(companyAssessment?.tier
                            ? { bg: tierBg(companyAssessment.tier), border: `1.5px solid ${tierColor(companyAssessment.tier)}` }
                            : { bg: "transparent", border: "1.5px solid", borderColor: "border.default" }
                          )}
                        >
                          <Text
                            fontSize="11px"
                            fontWeight="600"
                            letterSpacing="0.02em"
                            color={companyAssessment?.tier ? tierColor(companyAssessment.tier) : "text.muted"}
                          >
                            {companyAssessment?.overallScore != null
                              ? `Tier ${companyAssessment.tier} — ${companyAssessment.overallScore}/100${companyAssessment.capTableGateFailed ? " ⚠" : ""}`
                              : "Score Card"}
                          </Text>
                        </Box>
                      </VStack>

                      <VStack gap={0.5} alignItems="flex-start">
                        <Text fontSize="10px" fontWeight="600" color="text.muted" letterSpacing="0.05em" textTransform="uppercase">Deal</Text>
                        <Box
                          display="inline-flex"
                          alignItems="center"
                          px={3}
                          py={1}
                          borderRadius="full"
                          cursor="pointer"
                          onClick={() => setDealAssessmentOpen(true)}
                          transition="all 0.15s"
                          _hover={{ opacity: 0.8 }}
                          {...(latestDealAssessment?.tier
                            ? { bg: tierBg(latestDealAssessment.tier), border: `1.5px solid ${tierColor(latestDealAssessment.tier)}` }
                            : { bg: "transparent", border: "1.5px solid", borderColor: "border.default" }
                          )}
                        >
                          <Text
                            fontSize="11px"
                            fontWeight="600"
                            letterSpacing="0.02em"
                            color={latestDealAssessment?.tier ? tierColor(latestDealAssessment.tier) : "text.muted"}
                          >
                            {latestDealAssessment?.overallScore != null
                              ? `Tier ${latestDealAssessment.tier} — ${latestDealAssessment.overallScore}/100`
                              : "Score Card"}
                          </Text>
                        </Box>
                      </VStack>
                    </HStack>

                    <HStack gap={2} flexWrap="wrap">
                      {isGreat && (
                        <Box
                          display="inline-flex"
                          alignItems="center"
                          px={3}
                          py={1}
                          borderRadius="full"
                          bg="rgba(234,179,8,0.12)"
                          border="1.5px solid rgba(234,179,8,0.6)"
                          w="fit-content"
                        >
                          <Text fontSize="11px" color="#eab308" fontWeight="700" letterSpacing="0.05em">
                            SIGNS OF GREATNESS
                          </Text>
                        </Box>
                      )}
                      {companyInfo.hotlist?.isActive && (
                        <Box
                          display="inline-flex"
                          alignItems="center"
                          px={3}
                          py={1}
                          borderRadius="full"
                          bg="rgba(239,68,68,0.12)"
                          border="1.5px solid rgba(239,68,68,0.6)"
                          w="fit-content"
                        >
                          <Text fontSize="11px" color="#ef4444" fontWeight="700" letterSpacing="0.05em">
                            ON HOTLIST
                          </Text>
                        </Box>
                      )}
                    </HStack>

                    {companyAssessment?.investmentOneLiner && (
                      <Text fontSize="xs" color="text.muted" fontStyle="italic" lineHeight="1.5">
                        {companyAssessment.investmentOneLiner}
                      </Text>
                    )}

                    <Separator />

                    {/* Action buttons */}
                    <Stack gap={2}>
                      <Button
                        size="xs"
                        variant="outline"
                        alignSelf="flex-start"
                        px={4}
                        onClick={handleDownloadMemo}
                        loading={isDownloadingMemo}
                        loadingText="Generating..."
                      >
                        Deal Memo
                      </Button>

                      {canSendPortalEmail && !deal.client?.introEmailSentAt && (
                        <Button
                          size="xs"
                          variant="outline"
                          alignSelf="flex-start"
                          onClick={() => setIsIntroEmailConfirmOpen(true)}
                          loading={isSendingIntroEmail}
                          loadingText="Sending..."
                        >
                          Send Portal Welcome Email
                        </Button>
                      )}
                      {deal.client?.introEmailSentAt && (
                        <Text fontSize="xs" color="green.600" _dark={{ color: "green.300" }}>
                          Portal email sent {new Date(deal.client.introEmailSentAt).toLocaleDateString()}
                        </Text>
                      )}

                      {/* Confirmation dialog for portal email */}
                      <Dialog.Root open={isIntroEmailConfirmOpen} onOpenChange={(d) => { if (!d.open) setIsIntroEmailConfirmOpen(false) }}>
                        <Portal>
                          <Dialog.Backdrop />
                          <Dialog.Positioner>
                            <Dialog.Content maxW="400px">
                              <Dialog.Header>
                                <Dialog.Title>Send Portal Welcome Email</Dialog.Title>
                              </Dialog.Header>
                              <Dialog.Body>
                                <Text fontSize="sm">
                                  Send the portal welcome email to <strong>{deal.client?.firstName} {deal.client?.lastName}</strong>? This will give them access to the client portal.
                                </Text>
                              </Dialog.Body>
                              <Dialog.Footer>
                                <Button variant="outline" size="sm" onClick={() => setIsIntroEmailConfirmOpen(false)}>
                                  Cancel
                                </Button>
                                <Button colorPalette="purple" size="sm" onClick={() => { setIsIntroEmailConfirmOpen(false); handleSendIntroEmail() }}>
                                  Send Email
                                </Button>
                              </Dialog.Footer>
                            </Dialog.Content>
                          </Dialog.Positioner>
                        </Portal>
                      </Dialog.Root>
                    </Stack>
                  </Stack>
                </Tabs.Content>

                {/* Client Info tab */}
                <Tabs.Content value="client-info">
                  <Box pt={3}>
                    <DealClientInfoView
                      client={deal.client}
                      clientInfo={deal.clientInfo}
                      onUpdate={handleClientInfoUpdate}
                      onClientUpdate={handleClientUpdate}
                      inline
                    />
                  </Box>
                </Tabs.Content>

              </Tabs.Root>
            </Stack>
          </Box>

          {/* ── Center Panel: Deal Content ───────────────────────────────── */}
          <Box
            ref={sectionRef}
            flex={1}
            overflowY="auto"
            pl={6}
            pr={6}
            pt={4}
            pb={10}
          >
            <CompanyTabs
              companyInfo={companyInfo}
              employeeCounts={companyInfo.employeeCounts || []}
              deal={deal}
              onRefreshGrants={handleRefreshGrants}
              isRefreshingGrants={isExtracting}
              dealPageMode={true}
              onSyncClick={() => setIsSyncDialogOpen(true)}
              isSyncing={isSyncing}
              onCalcToggle={() => setIsCalcOpen((v) => !v)}
              isCalcOpen={isCalcOpen}
            />
          </Box>

          {/* ── Right Panel: Calculator ──────────────────────────────────── */}
          {isCalcOpen && (
            <Box
              w="300px"
              minW="300px"
              borderLeftWidth="1px"
              borderColor="border.default"
              overflowY="auto"
              flexShrink={0}
            >
              <Stack p={4} gap={3}>
                <HStack justify="space-between">
                  <Text fontSize="sm" fontWeight="semibold">Quick Calculator</Text>
                  <IconButton
                    aria-label="Close calculator"
                    size="xs"
                    variant="ghost"
                    onClick={() => setIsCalcOpen(false)}
                  >
                    <LuX />
                  </IconButton>
                </HStack>

                <Text fontSize="xs" color="text.muted">
                  Enter a number or math expression. Reference other rows with $Label.
                </Text>

                <Stack gap={2}>
                  {calcCells.map((cell) => {
                    const result = calcValues[cell.id]
                    return (
                      <HStack key={cell.id} gap={1} align="center">
                        <Input
                          size="xs"
                          placeholder="Label"
                          value={cell.label}
                          onChange={(e) => updateCalcCell(cell.id, "label", e.target.value)}
                          w="80px"
                          minW="80px"
                          flexShrink={0}
                        />
                        <Text fontSize="xs" color="text.muted" flexShrink={0}>=</Text>
                        <Input
                          size="xs"
                          placeholder="0"
                          value={cell.formula}
                          onChange={(e) => updateCalcCell(cell.id, "formula", e.target.value)}
                          flex={1}
                          minW={0}
                        />
                        <Text
                          fontSize="xs"
                          fontWeight="semibold"
                          color={result !== null ? "green.600" : "text.muted"}
                          minW="55px"
                          textAlign="right"
                          flexShrink={0}
                          _dark={{ color: result !== null ? "green.300" : "text.muted" }}
                        >
                          {formatCalcResult(result)}
                        </Text>
                        <IconButton
                          aria-label="Remove row"
                          size="2xs"
                          variant="ghost"
                          colorPalette="red"
                          flexShrink={0}
                          onClick={() => removeCalcCell(cell.id)}
                        >
                          <LuX />
                        </IconButton>
                      </HStack>
                    )
                  })}
                </Stack>

                <HStack gap={2}>
                  <Button size="xs" variant="outline" onClick={addCalcCell} flex={1}>
                    + Add Row
                  </Button>
                  <Button
                    size="xs"
                    variant="ghost"
                    colorPalette="gray"
                    onClick={() => setCalcCells(DEFAULT_CALC_CELLS)}
                    flex={1}
                  >
                    Reset
                  </Button>
                </HStack>

                <Separator />

                <Text fontSize="xs" color="text.muted" lineHeight="1.6">
                  <Text as="span" fontWeight="semibold">Tips:</Text>{" "}
                  Use standard math: +, -, *, /, (). Reference another row by typing $Label (e.g., <Text as="span" fontFamily="mono">$PP * $Shares</Text>).
                </Text>
              </Stack>
            </Box>
          )}

        </Box>
      </Box>

      {/* ── Floating Action Buttons ──────────────────────────────────────────── */}
      <HStack position="fixed" bottom={6} right={6} gap={3}>
        <CompanyFlags companyInfo={companyInfo} />
        <IconButton
          aria-label="Activity log"
          size="lg"
          borderRadius={10}
          boxShadow="lg"
          bg="fab.bg"
          onClick={() => setIsDrawerOpen(true)}
        >
          <FaHistory color="fab.icon" />
        </IconButton>
        <IconButton
          aria-label="Scroll to top"
          size="lg"
          borderRadius={10}
          boxShadow="lg"
          bg="fab.bg"
          onClick={scrollToTop}
        >
          <FaArrowUp color="fab.icon" />
        </IconButton>
      </HStack>

      {/* ── Activity Log Drawer ──────────────────────────────────────────────── */}
      <Drawer.Root open={isDrawerOpen} onOpenChange={(e) => setIsDrawerOpen(e.open)} placement="end" size="md">
        <Portal>
          <Drawer.Backdrop />
          <Drawer.Positioner>
            <Drawer.Content>
              <Drawer.Header borderBottomWidth="1px">
                <Drawer.Title>Activity Log</Drawer.Title>
                <Drawer.CloseTrigger />
              </Drawer.Header>
              <Drawer.Body>
                {combinedLogs.length === 0 ? (
                  <Text fontSize="sm" color="text.muted" mt={4}>No activity yet.</Text>
                ) : (
                  <Stack gap={0} mt={2}>
                    {combinedLogs.map((log, i) => (
                      <Stack key={log.id} gap={1} py={3}>
                        <HStack gap={2}>
                          <Text
                            fontSize="2xs"
                            fontWeight="bold"
                            color={log.type === "deal" ? "blue.500" : log.type === "hotlist" ? "orange.500" : "green.500"}
                            textTransform="uppercase"
                          >
                            {log.type === "deal" ? "Deal" : log.type === "hotlist" ? "Hotlist" : "Company"}
                          </Text>
                          <Text fontSize="2xs" color="text.muted">
                            {formatLogDate(log.createdAt)}
                          </Text>
                          {log.by && (
                            <Text fontSize="2xs" color="text.muted">
                              by {log.by}
                            </Text>
                          )}
                        </HStack>
                        <Text fontSize="sm">{log.description}</Text>
                        {i < combinedLogs.length - 1 && <Separator mt={2} />}
                      </Stack>
                    ))}
                  </Stack>
                )}
              </Drawer.Body>
            </Drawer.Content>
          </Drawer.Positioner>
        </Portal>
      </Drawer.Root>

      {/* ── Sync from Legacy dialog ──────────────────────────────────────────── */}
      <Dialog.Root open={isSyncDialogOpen} onOpenChange={(e) => !e.open && setIsSyncDialogOpen(false)}>
        <Portal>
          <Dialog.Backdrop />
          <Dialog.Positioner>
            <Dialog.Content>
              <VStack p={10} gap={5}>
                <Text fontSize="2xl" fontWeight="bold">Sync from Legacy DB?</Text>
                <Text textAlign="center">
                  This will pull the latest data from the legacy database for this deal. Existing deal data may be overwritten.
                </Text>
                <HStack mt={5}>
                  <Button
                    variant="solid"
                    onClick={handleSyncFromLegacy}
                    disabled={isSyncing}
                  >
                    {isSyncing ? "Syncing..." : "Confirm"}
                  </Button>
                  <Button variant="outline" onClick={() => setIsSyncDialogOpen(false)} disabled={isSyncing}>
                    Cancel
                  </Button>
                </HStack>
              </VStack>
            </Dialog.Content>
          </Dialog.Positioner>
        </Portal>
      </Dialog.Root>

      {/* ── Assessment Panels ────────────────────────────────────────────────── */}
      <CompanyAssessmentPanel
        companyId={companyInfo.id}
        companyName={companyInfo.name}
        isOpen={assessmentOpen}
        onClose={() => setAssessmentOpen(false)}
        onAssessmentComplete={() => {
          queryClient.invalidateQueries({ queryKey: ["companyInfo", deal.companyId] })
        }}
      />

      <DealAssessmentPanel
        dealId={deal.id}
        dealIdStr={deal.dealIdStr}
        companyName={companyInfo.name}
        isOpen={dealAssessmentOpen}
        onClose={() => setDealAssessmentOpen(false)}
        onAssessmentComplete={() => {
          queryClient.invalidateQueries({ queryKey: ["dealInfo", dealId] })
        }}
      />

      {/* ── Grant Extraction Preview dialog ─────────────────────────────────── */}
      <Dialog.Root open={!!grantPreview} onOpenChange={(e) => !e.open && setGrantPreview(null)}>
        <Portal>
          <Dialog.Backdrop />
          <Dialog.Positioner>
            <Dialog.Content maxW="950px">
              <VStack p={8} gap={4} align="stretch">
                <Text fontSize="xl" fontWeight="bold">Grant Extraction Preview</Text>
                <Text fontSize="sm" color="text.muted">
                  Extracted from {grantPreview?.fileCount ?? 0} vesting document(s)
                </Text>

                {grantPreview && grantPreview.grants.length === 0 && !grantPreview.fmv && (
                  <Text fontSize="sm" color="text.muted" mt={2}>
                    No qualified grants or FMV values were found in the vesting documents.
                  </Text>
                )}

                {grantPreview && editableGrants.length > 0 && (
                  <Box overflowX="auto">
                    <Table.Root size="sm">
                      <Table.Header>
                        <Table.Row>
                          <Table.ColumnHeader>Source</Table.ColumnHeader>
                          <Table.ColumnHeader>Description</Table.ColumnHeader>
                          <Table.ColumnHeader>Type</Table.ColumnHeader>
                          <Table.ColumnHeader textAlign="right">Vested</Table.ColumnHeader>
                          <Table.ColumnHeader textAlign="right">Strike</Table.ColumnHeader>
                          <Table.ColumnHeader>Date</Table.ColumnHeader>
                          <Table.ColumnHeader textAlign="right">Est. Value</Table.ColumnHeader>
                          <Table.ColumnHeader textAlign="right">Tax ($)</Table.ColumnHeader>
                          <Table.ColumnHeader>Status</Table.ColumnHeader>
                        </Table.Row>
                      </Table.Header>
                      <Table.Body>
                        {editableGrants.map((grant, i) => (
                          <Table.Row key={i}>
                            <Table.Cell>
                              <Text fontSize="xs" maxW="100px" truncate title={grant.source}>
                                {grant.source}
                              </Text>
                            </Table.Cell>
                            <Table.Cell>
                              <Input
                                size="xs"
                                value={grant.grantDescription || ""}
                                onChange={(e) => updateEditableGrant(i, { grantDescription: e.target.value || null })}
                                maxW="120px"
                              />
                            </Table.Cell>
                            <Table.Cell>
                              <Badge size="sm" colorPalette={grant.grantType === "ISO" ? "blue" : grant.grantType === "NSO" ? "purple" : "gray"}>
                                {grant.grantType.toUpperCase()}
                              </Badge>
                            </Table.Cell>
                            <Table.Cell textAlign="right">
                              <Input
                                size="xs"
                                type="number"
                                value={grant.vestedShares ?? ""}
                                onChange={(e) => updateEditableGrant(i, { vestedShares: e.target.value ? Number(e.target.value) : null })}
                                maxW="80px"
                                textAlign="right"
                              />
                            </Table.Cell>
                            <Table.Cell textAlign="right">
                              <Input
                                size="xs"
                                type="number"
                                step="0.01"
                                value={grant.pricePerShare ?? ""}
                                onChange={(e) => updateEditableGrant(i, { pricePerShare: e.target.value ? Number(e.target.value) : null })}
                                maxW="80px"
                                textAlign="right"
                              />
                            </Table.Cell>
                            <Table.Cell>
                              <Input
                                size="xs"
                                value={grant.grantDate || ""}
                                onChange={(e) => updateEditableGrant(i, { grantDate: e.target.value || null })}
                                maxW="100px"
                              />
                            </Table.Cell>
                            <Table.Cell textAlign="right">
                              {grant.grantType === "NSO" ? (
                                <Input
                                  size="xs"
                                  type="number"
                                  step="0.01"
                                  value={grant.estimatedOptionsValue ?? ""}
                                  onChange={(e) => updateEditableGrant(i, { estimatedOptionsValue: e.target.value ? Number(e.target.value) : null })}
                                  maxW="90px"
                                  textAlign="right"
                                />
                              ) : (
                                <Text fontSize="xs" color="text.muted">—</Text>
                              )}
                            </Table.Cell>
                            <Table.Cell textAlign="right">
                              {grant.grantType === "NSO" ? (
                                <Input
                                  size="xs"
                                  type="number"
                                  step="0.01"
                                  value={grant.tax ?? ""}
                                  onChange={(e) => updateEditableGrant(i, { tax: e.target.value ? Number(e.target.value) : null })}
                                  maxW="80px"
                                  textAlign="right"
                                />
                              ) : (
                                <Text fontSize="xs" color="text.muted">—</Text>
                              )}
                            </Table.Cell>
                            <Table.Cell>
                              <Badge size="sm" colorPalette={grant.existingGrantId ? "orange" : "green"}>
                                {grant.existingGrantId ? "Update" : "New"}
                              </Badge>
                            </Table.Cell>
                          </Table.Row>
                        ))}
                      </Table.Body>
                    </Table.Root>
                  </Box>
                )}

                {grantPreview?.fmv && (
                  <Box borderWidth="1px" borderColor="border.default" borderRadius="md" p={3} mt={1}>
                    <HStack justify="space-between">
                      <Text fontSize="sm" fontWeight="medium">409A FMV</Text>
                      <HStack gap={2}>
                        <Text fontSize="sm" fontWeight="bold">${grantPreview.fmv.fmv.toFixed(2)}</Text>
                        {grantPreview.fmv.date && (
                          <Text fontSize="xs" color="text.muted">{grantPreview.fmv.date}</Text>
                        )}
                      </HStack>
                    </HStack>
                  </Box>
                )}

                <HStack mt={4} justify="flex-end" gap={3}>
                  <Button variant="outline" onClick={() => setGrantPreview(null)} disabled={isConfirming}>
                    Cancel
                  </Button>
                  <Button
                    variant="solid"
                    onClick={handleConfirmGrants}
                    disabled={isConfirming || (grantPreview?.grants.length === 0 && !grantPreview?.fmv)}
                  >
                    {isConfirming ? <><Spinner size="xs" mr={2} /> Saving...</> : "Confirm"}
                  </Button>
                </HStack>
              </VStack>
            </Dialog.Content>
          </Dialog.Positioner>
        </Portal>
      </Dialog.Root>
    </>
  )
}
